import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ShieldIcon, ZapIcon, DropletsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ServicePageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ServicePageTemplate({
  setPage
}: ServicePageProps) {
  // In a real app, this data would be passed in via props or fetched based on a slug.
  // This template uses "Dustless Blasting" as the example content.
  const serviceData = {
    title: 'Dustless Blasting Services',
    subtitle: 'Low-mess, high-efficiency surface preparation for sensitive environments and tough coatings.',
    heroImage: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=1920&auto=format&fit=crop',
    overview: 'Our mobile dustless blasting equipment employs a mixture of water, air, and abrasive to clean and remove surface contaminants and coatings. The addition of water suppresses up to 95% of airborne dust, making it the ideal choice for urban areas, active job sites, and projects requiring strict environmental compliance.',
    benefits: ['Suppresses 95% of airborne dust', 'Requires significantly less containment', "Reduces heat friction (won't warp thin metal)", 'EPA and DEQ conscious process', 'Leaves a clean, paint-ready surface', 'Safe for use around other workers'],
    applications: [{
      name: 'Automotive Restoration',
      desc: 'Strips paint and body filler without warping sheet metal.'
    }, {
      name: 'Brick & Concrete',
      desc: 'Removes graffiti, old paint, and grime without damaging the substrate.'
    }, {
      name: 'Marine & Boats',
      desc: 'Strips bottom paint and gelcoat safely and efficiently.'
    }, {
      name: 'Industrial Equipment',
      desc: 'Cleans heavy machinery on-site with minimal disruption.'
    }]
  };
  return <div className="w-full bg-white" data-id="element-557">
      {/* Hero Section */}
      <section className="relative min-h-[400px] md:min-h-[500px] flex items-center bg-charcoal" data-id="element-558">
        <div className="absolute inset-0 z-0" data-id="element-559">
          <img src={serviceData.heroImage} alt={serviceData.title} className="w-full h-full object-cover opacity-30" data-id="element-560" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-transparent" data-id="element-561"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full pt-20 pb-16" data-id="element-562">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl" data-id="element-563">
            <motion.div variants={fadeInUp} className="mb-4" data-id="element-564">
              <span className="text-brand-red font-heading font-bold uppercase tracking-widest text-sm" data-id="element-565">
                Core Service
              </span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase leading-[1.1] mb-6" data-id="element-566">
              {serviceData.title}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-steel-200 font-body mb-8 leading-relaxed" data-id="element-567">
              {serviceData.subtitle}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4" data-id="element-568">
              <Button variant="primary" onClick={() => setPage('contact')} data-id="element-569">
                Get a Quote for this Service
              </Button>
              <Button variant="outline" href="tel:5177778088" showPhoneIcon data-id="element-570">
                Call (517) 777-8088
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Overview & Benefits */}
      <section className="py-20 px-4" data-id="element-571">
        <div className="max-w-7xl mx-auto" data-id="element-572">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-573">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-574">
              <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-575">
                The Smarter Way to Strip & Clean
              </h2>
              <p className="text-steel-600 text-lg leading-relaxed mb-8" data-id="element-576">
                {serviceData.overview}
              </p>

              <div className="space-y-4" data-id="element-577">
                {serviceData.benefits.map((benefit, idx) => <motion.div key={idx} variants={fadeInUp} className="flex items-start" data-id="element-578">
                    <CheckCircleIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0 mt-0.5" data-id="element-579" />
                    <span className="text-charcoal font-medium" data-id="element-580">{benefit}</span>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative h-[400px] md:h-[500px] rounded-sm overflow-hidden shadow-xl" data-id="element-581">
              <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000&auto=format&fit=crop" alt="Dustless blasting in action" className="w-full h-full object-cover" data-id="element-582" />
              <div className="absolute bottom-0 left-0 right-0 bg-charcoal/90 backdrop-blur-sm p-6 border-t-4 border-brand-red" data-id="element-583">
                <div className="flex items-center text-white" data-id="element-584">
                  <DropletsIcon className="w-8 h-8 text-brand-red mr-4" data-id="element-585" />
                  <div data-id="element-586">
                    <p className="font-heading font-bold uppercase text-lg" data-id="element-587">
                      95% Less Dust
                    </p>
                    <p className="text-steel-300 text-sm" data-id="element-588">
                      Compared to traditional dry blasting
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Applications Grid */}
      <section className="py-20 bg-steel-50 px-4" data-id="element-589">
        <div className="max-w-7xl mx-auto" data-id="element-590">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-591">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-592">
              Common Applications
            </h2>
            <p className="text-steel-600 max-w-2xl mx-auto" data-id="element-593">
              Dustless blasting is versatile enough to handle delicate
              restorations and aggressive enough for heavy industrial coatings.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-594">
            {serviceData.applications.map((app, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 border border-steel-200 hover:border-brand-red transition-colors group" data-id="element-595">
                <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3 group-hover:text-brand-red transition-colors" data-id="element-596">
                  {app.name}
                </h3>
                <p className="text-steel-600 leading-relaxed" data-id="element-597">{app.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-red text-white text-center px-4" data-id="element-598">
        <div className="max-w-3xl mx-auto" data-id="element-599">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase mb-6" data-id="element-600">
            Need {serviceData.title} in Michigan?
          </h2>
          <p className="text-lg mb-8 text-white/90" data-id="element-601">
            We are fully mobile and bring our equipment directly to your
            location. Fast estimates and quick turnaround.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-602">
            <Button variant="secondary" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto border-none text-brand-red" data-id="element-603">
              Request a Quote
            </Button>
            <Button variant="outline-red" size="lg" href="tel:5177778088" className="w-full sm:w-auto" data-id="element-604">
              Call (517) 777-8088
            </Button>
          </div>
        </div>
      </section>
    </div>;
}